import axios from 'axios';

const APPLICATION_SERVICE_BASE_URL = import.meta.env.VITE_APPLICATION_SERVICE_URL || 'http://localhost:8003';

/**
 * Application data interface for recruiters
 */
export interface Application {
    id: string;
    jobId: string;
    candidateId: string;
    recruiterId?: string;
    currentStage: 'applied' | 'accepted' | 'interview' | 'offered' | 'rejected';
    resumeUrl?: string;
    coverLetter?: string;
    appliedAt: string;
    updatedAt: string;
    candidateName?: string;
    candidateEmail?: string;
    jobTitle?: string;
    companyName?: string;
}

export interface UpdateStageRequest {
    stage: 'applied' | 'accepted' | 'interview' | 'offered' | 'rejected';
    notes?: string;
}

/**
 * Recruiter Application API Service
 * Handles all interactions with the application-service-go backend for recruiters
 */
class RecruiterApplicationApiService {
    /**
     * Get auth token from localStorage
     */
    private getAuthToken(): string | null {
        return localStorage.getItem('authToken');
    }

    /**
     * Get auth headers with Bearer token
     */
    private getAuthHeaders(): Record<string, string> {
        const token = this.getAuthToken();
        if (!token) {
            throw new Error('No authentication token found');
        }

        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        };
    }

    /**
     * Get all applications for a specific job
     * @param jobId Job ID
     * @returns Promise<Application[]>
     */
    async getJobApplications(jobId: string): Promise<Application[]> {
        try {
            const response = await axios.get<{
                payload: { data: Application[]; count: number };
                status: { msg: string; error: boolean };
            }>(
                `${APPLICATION_SERVICE_BASE_URL}/applications/v1/job/${jobId}`,
                {
                    headers: this.getAuthHeaders(),
                }
            );

            // Handle Go service response format: { payload: { data: [...] }, status: {...} }
            return response.data.payload?.data || [];
        } catch (error) {
            console.error('Error fetching job applications:', error);
            throw error;
        }
    }

    /**
     * Update application stage
     * @param applicationId Application ID
     * @param request Stage update request
     * @returns Promise<Application>
     */
    async updateApplicationStage(applicationId: string, request: UpdateStageRequest): Promise<Application> {
        try {
            const response = await axios.patch<{
                payload: { success: boolean; data: Application };
                status: { msg: string; error: boolean };
            }>(
                `${APPLICATION_SERVICE_BASE_URL}/applications/v1/${applicationId}/stage`,
                request,
                {
                    headers: this.getAuthHeaders(),
                }
            );

            // Handle Go service response format
            return response.data.payload?.data;
        } catch (error: any) {
            console.error('Error updating application stage:', error);
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            throw new Error('Failed to update application stage');
        }
    }

    /**
     * Get a specific application by ID
     * @param applicationId Application ID
     * @returns Promise<Application>
     */
    async getApplication(applicationId: string): Promise<Application> {
        try {
            const response = await axios.get<{
                payload: Application;
                status: { msg: string; error: boolean };
            }>(
                `${APPLICATION_SERVICE_BASE_URL}/applications/v1/${applicationId}`,
                {
                    headers: this.getAuthHeaders(),
                }
            );

            // Handle Go service response format
            return response.data.payload;
        } catch (error) {
            console.error('Error fetching application:', error);
            throw error;
        }
    }
}

export const recruiterApplicationApiService = new RecruiterApplicationApiService();
